import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CatalogEntryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { denominationsApi } from '@/services/denominations.api';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';
import { EntityLogo } from '@/components/entity-logo';

const QUERY_KEY = ['platform', 'denominations'];

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * Mantenimientos — the shared, platform-wide denomination catalog every
 * church picks from on its own profile. `PLATFORM_SUPER_ADMIN` only; not a
 * tenant resource, the same posture the module catalog already takes.
 */
export function DenominationsPage(): ReactNode {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  // Controlled exclusively by My Settings — no per-page override, so an
  // archived record is never one accidental checkbox away from view.
  const showArchived = getShowArchivedRecordsPreference();

  const entries = useQuery({
    queryKey: [...QUERY_KEY, showArchived],
    queryFn: () => denominationsApi.list(showArchived),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: false });

  const list = entries.data ?? [];
  const canCreate = can('platform.denomination.create');

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('platform.denominations.title')}</h1>
          <p className="page-count">{t('platform.denominations.subtitle')}</p>
        </div>
      </header>

      {canCreate && <CreateEntryForm onCreated={() => void invalidate()} />}

      {entries.isPending && <Spinner label={t('common.states.loading')} />}
      {entries.isSuccess && list.length === 0 && (
        <EmptyState title={t('platform.denominations.empty')} />
      )}

      {list.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('common.labels.logo')}</th>
                <th scope="col">{t('platform.denominations.name')}</th>
                <th scope="col">{t('common.labels.abbreviation')}</th>
                <th scope="col">{t('common.labels.status')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {list.map((entry) => (
                <EntryRow key={entry.id} entry={entry} onChanged={() => void invalidate()} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateEntryForm({ onCreated }: { readonly onCreated: () => void }) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      denominationsApi.create({ name: name.trim(), abbreviation: abbreviation.trim() || null }),
    onSuccess: () => {
      setName('');
      setAbbreviation('');
      setError(null);
      onCreated();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  return (
    <form
      className="filters"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (name.trim()) create.mutate();
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <Field
        label={t('platform.denominations.name')}
        name="name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
      />
      <Field
        label={t('common.labels.abbreviation')}
        name="abbreviation"
        value={abbreviation}
        onChange={(event) => setAbbreviation(event.target.value)}
        maxLength={20}
        dir="ltr"
      />
      <Button type="submit" variant="primary" loading={create.isPending}>
        {t('platform.denominations.new')}
      </Button>
    </form>
  );
}

function EntryRow({
  entry,
  onChanged,
}: {
  readonly entry: CatalogEntryResponse;
  readonly onChanged: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(entry.name);
  const [abbreviation, setAbbreviation] = useState(entry.abbreviation ?? '');
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () =>
      denominationsApi.update(entry.id, {
        name: name.trim(),
        abbreviation: abbreviation.trim() || null,
      }),
    onSuccess: () => {
      setError(null);
      setIsEditing(false);
      onChanged();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const session = await filesApi.requestUpload({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        declaredSize: file.size,
        ownerType: 'Denomination',
        ownerId: entry.id,
      });
      await filesApi.putBytes(session.uploadUrl, file);
      const uploaded = await filesApi.completeUpload(session.id, {
        fileName: file.name,
        ownerType: 'Denomination',
        ownerId: entry.id,
      });
      return denominationsApi.update(entry.id, { logoFileId: uploaded.id });
    },
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const archive = useMutation({
    mutationFn: () => denominationsApi.archive(entry.id),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const restore = useMutation({
    mutationFn: () => denominationsApi.restore(entry.id),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const canUpdate = can('platform.denomination.update');
  const isArchived = entry.archivedAt !== null;

  if (isEditing) {
    return (
      <tr>
        <td colSpan={5}>
          {error && <Alert tone="danger">{error}</Alert>}
          <label className="logo-upload" aria-label={t('common.labels.changeLogo')}>
            <EntityLogo logoFileId={entry.logoFileId} size={48} />
            <span className="logo-upload-hint">{t('common.labels.changeLogo')}</span>
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) uploadLogo.mutate(file);
              }}
            />
          </label>
          <form
            className="filters"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              update.mutate();
            }}
          >
            <Field
              label={t('platform.denominations.name')}
              name={`edit-name-${entry.id}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <Field
              label={t('common.labels.abbreviation')}
              name={`edit-abbreviation-${entry.id}`}
              value={abbreviation}
              onChange={(event) => setAbbreviation(event.target.value)}
              maxLength={20}
              dir="ltr"
            />
            <Button type="submit" variant="primary" loading={update.isPending}>
              {t('common.actions.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setIsEditing(false)}>
              {t('common.actions.cancel')}
            </Button>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <EntityLogo logoFileId={entry.logoFileId} size={32} />
      </td>
      <td>{entry.name}</td>
      <td>{entry.abbreviation ?? <span className="cell-empty">—</span>}</td>
      <td>{t(isArchived ? 'platform.denominations.archived' : 'platform.denominations.active')}</td>
      <td>
        {error && <Alert tone="danger">{error}</Alert>}
        {canUpdate && !isArchived && (
          <Button variant="ghost" onClick={() => setIsEditing(true)}>
            {t('common.actions.edit')}
          </Button>
        )}
        {canUpdate && !isArchived && (
          <Button variant="ghost" loading={archive.isPending} onClick={() => archive.mutate()}>
            {t('common.actions.archive')}
          </Button>
        )}
        {canUpdate && isArchived && (
          <Button variant="ghost" loading={restore.isPending} onClick={() => restore.mutate()}>
            {t('common.actions.restore')}
          </Button>
        )}
      </td>
    </tr>
  );
}
